import React, { useState, useEffect, useCallback } from 'react';
import { message, Modal } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import PromptList from './components/PromptList';
import PromptDetail from './components/PromptDetail';
import PromptEditor from './components/PromptEditor';
import VersionCompare from './components/VersionCompare';
import VersionTimeline from './components/VersionTimeline';
import LandingPage from './components/LandingPage';
import BackupReminder from './components/BackupReminder';
import CloudBackup from './components/CloudBackup';
import SharedPromptView from './components/SharedPromptView';
import DB from './services/dbService';
import promptUpdateService from './services/promptUpdateService';
import CloudBackupService from './services/cloudBackupService';
import './modern-ui.css';
import './loading.css';
import './enhanced-ui.css';

// 主应用组件
function MainApp() {
  // 状态管理
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [timelineMode, setTimelineMode] = useState(false);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [compareVersions, setCompareVersions] = useState({
    leftVersionIndex: 0,
    rightVersionIndex: 0
  });
  const [isLoading, setIsLoading] = useState(true); 
  
  // 收藏功能相关状态
  const [favoritePrompts, setFavoritePrompts] = useState([]);
  const [selectedFavorite, setSelectedFavorite] = useState(null);
  const [viewingFavorites, setViewingFavorites] = useState(false);
  
  // 更新提示词的信息
  const handleUpdatePrompt = useCallback((updatedPrompt) => {
    promptUpdateService.updatePrompt(updatedPrompt, setPrompts);
  }, []);

  // 关闭标签页提示状态
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // 删除确认弹窗状态
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    title: '',
    content: '',
    onOk: null
  });
  
  // 云端备份弹窗状态
  const [cloudBackupModal, setCloudBackupModal] = useState({
    visible: false
  });

  // 显示删除确认弹窗
  const showDeleteConfirm = (title, content, onOk) => {
    setDeleteModal({
      visible: true,
      title,
      content,
      onOk
    });
  };

  // 处理删除确认
  const handleDeleteConfirm = async () => {
    if (!deleteModal.onOk) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await deleteModal.onOk();
      setDeleteModal(prev => ({ ...prev, visible: false }));
    } catch (error) {
      console.error('删除操作失败:', error);
    } finally {
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // 取消删除确认
  const handleDeleteCancel = () => {
    setDeleteModal(prev => ({ ...prev, visible: false }));
  };

  // 处理导出所有提示词
  const handleExportAll = async (format) => {
    try {
      setIsLoading(true);
      
      // 修复：我们怀疑可能有一个隐藏的分页限制，尝试直接从数据库查询所有记录
      console.log('=========== 开始导出流程 ===========');
      console.log('正在从数据库获取所有提示词数据...');
      
      // 获取数据库连接
      const db = await DB.getDB();
      
      // 手动查询所有提示词
      const manualPrompts = await new Promise((resolve, reject) => {
        const transaction = db.transaction(DB.STORES.PROMPTS, 'readonly');
        const store = transaction.objectStore(DB.STORES.PROMPTS);
        const request = store.getAll();
        
        request.onsuccess = (event) => {
          const results = event.target.result || [];
          console.log(`手动查询成功获取到 ${results.length} 个提示词`);
          resolve(results);
        };
        
        request.onerror = (event) => {
          console.error('手动查询提示词失败:', event.target.error);
          reject(event.target.error);
        };
      });
      
      // 使用标准方法查询
      const allPrompts = await DB.getAllItems(DB.STORES.PROMPTS);
      const allFolders = await DB.getAllItems(DB.STORES.FOLDERS);
      const allTags = await DB.getAllItems(DB.STORES.TAGS);
      
      console.log(`标准查询获取到 ${allPrompts?.length || 0} 个提示词`);
      console.log(`手动查询获取到 ${manualPrompts?.length || 0} 个提示词`);
      
      // 比较结果是否一致
      const isResultConsistent = allPrompts?.length === manualPrompts?.length;
      console.log(`查询结果是否一致: ${isResultConsistent ? '是' : '否'}`);
      
      if (!isResultConsistent) {
        console.warn('标准查询和手动查询的结果不一致，将使用手动查询的结果');
      }
      
      // 使用数量更多的结果集
      const promptsToExport = manualPrompts.length > allPrompts.length ? manualPrompts : allPrompts;
      
      if (!promptsToExport || promptsToExport.length === 0) {
        message.warning('没有提示词可导出');
        console.log('没有提示词可导出，取消导出操作');
        setIsLoading(false);
        return;
      }
      
      // 强制检查每个提示词对象的有效性
      const validPrompts = promptsToExport.filter(prompt => 
        prompt && 
        prompt.id && 
        prompt.versions && 
        Array.isArray(prompt.versions) && 
        prompt.versions.length > 0
      );
      
      if (validPrompts.length < promptsToExport.length) {
        console.warn(`过滤后有 ${validPrompts.length}/${promptsToExport.length} 个有效提示词`);
      }
      
      console.log(`准备导出 ${validPrompts.length} 个提示词...`);
      console.log('提示词示例:');
      console.log('第一个提示词:', validPrompts[0]?.title);
      console.log('最后一个提示词:', validPrompts[validPrompts.length - 1]?.title);
      
      // 创建一个临时的导出按钮并触发点击
      if (format === 'json') {
        // 使用所有提示词，而不是过滤后的提示词
        // 处理提示词数据，确保每个版本的结果图片被正确包含
        const processedPrompts = validPrompts.map(prompt => {
          // 确保每个版本的结果图片被包含
          const processedVersions = prompt.versions.map(version => {
            // 如果版本包含结果图片，确保完整导出
            return {
              ...version,
              resultImages: version.resultImages || []
            };
          });
          
          return {
            ...prompt,
            versions: processedVersions
          };
        });
        
        const exportData = {
          version: '1.1', // 版本号更新，表示支持图片导出
          exportedAt: new Date().toISOString(),
          prompts: processedPrompts,
          folders: allFolders,
          tags: allTags,
          includesImages: true // 标记此备份包含图片数据
        };
        
        // 创建Blob对象
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        
        // 创建下载链接
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `PromptPro_所有提示词_${new Date().toLocaleDateString()}.json`;
        
        // 触发下载
        document.body.appendChild(link);
        link.click();
        
        // 清理
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        message.success(`已成功导出 ${validPrompts.length} 个提示词`);
      } else if (format === 'zip') {
        // 确保在导出操作完成前不释放loading状态
        try {
          console.log('正在加载导出服务模块...');
          // 同步加载导出服务模块
          const exportService = await import('./utils/exportService');
          
          console.log('开始导出ZIP文件...');
          // 明确显示传递给导出服务的提示词数量
          console.log(`共传递 ${validPrompts.length} 个提示词给导出服务`);
          
          // 传入验证过的提示词列表
          const success = await exportService.exportAllPromptsAsZip(validPrompts, allFolders, allTags);
          
          if (success) {
            message.success(`已成功导出 ${validPrompts.length} 个提示词`);
          } else {
            message.error('导出失败，请稍后重试');
          }
        } catch (error) {
          console.error('导出错误:', error);
          message.error('导出过程中发生错误: ' + error.message);
        }
      }
      
      console.log('=========== 导出流程结束 ===========');
    } catch (error) {
      console.error('导出过程中发生错误:', error);
      message.error('导出失败，请稍后重试: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理云端备份
  const handleCloudBackup = () => {
    // 显示云端备份弹窗
    setCloudBackupModal({
      visible: true
    });
  };
  
  // 处理云端恢复
  const handleCloudRestore = (restoredData) => {
    try {
      console.log('从云端恢复数据:', restoredData);
      
      // 统计导入和跳过的数量
      let importStats = {
        prompts: { total: 0, imported: 0, skipped: 0 },
        folders: { total: 0, imported: 0, skipped: 0 },
        tags: { total: 0, imported: 0, skipped: 0 }
      };
      
      // 恢复提示词
      if (restoredData.prompts && Array.isArray(restoredData.prompts)) {
        importStats.prompts.total = restoredData.prompts.length;
        
        // 创建ID映射和标题映射，避免ID冲突和标题重复
        const existingPromptIds = new Set(prompts.map(p => p.id));
        const existingPromptTitles = new Map(prompts.map(p => [p.title?.toLowerCase() || '', p]));
        
        const importPrompts = restoredData.prompts.filter(prompt => {
          // 如果标题为空，允许导入
          if (!prompt.title) return true;
          
          // 如果已经存在相同标题的提示词，跳过
          const existingPrompt = existingPromptTitles.get(prompt.title.toLowerCase());
          if (existingPrompt) {
            importStats.prompts.skipped++;
            return false;
          }
          return true;
        }).map(prompt => {
          // 如果ID已存在，生成新ID
          const newId = existingPromptIds.has(prompt.id) ? uuidv4() : prompt.id;
          existingPromptIds.add(newId);
          
          return {
            ...prompt,
            id: newId
          };
        });
        
        importStats.prompts.imported = importPrompts.length;
        
        if (importPrompts.length > 0) {
          setPrompts(prevPrompts => [...prevPrompts, ...importPrompts]);
        }
      }
      
      // 恢复文件夹
      if (restoredData.folders && Array.isArray(restoredData.folders)) {
        importStats.folders.total = restoredData.folders.length;
        
        // 创建ID映射和名称映射，避免ID冲突和名称重复
        const existingFolderIds = new Set(folders.map(f => f.id));
        const existingFolderNames = new Map(folders.map(f => [f.name.toLowerCase(), f]));
        
        const processedFolders = restoredData.folders.filter(folder => {
          // 如果已经存在相同名称的文件夹，跳过
          const existingFolder = existingFolderNames.get(folder.name.toLowerCase());
          if (existingFolder) {
            importStats.folders.skipped++;
            return false;
          }
          return true;
        }).map(folder => {
          // 如果ID已存在，生成新ID
          const newId = existingFolderIds.has(folder.id) ? uuidv4() : folder.id;
          existingFolderIds.add(newId);
          
          return {
            ...folder,
            id: newId
          };
        });
        
        importStats.folders.imported = processedFolders.length;
        
        if (processedFolders.length > 0) {
          setFolders(prevFolders => [...prevFolders, ...processedFolders]);
        }
      }
      
      // 恢复标签
      if (restoredData.tags && Array.isArray(restoredData.tags)) {
        importStats.tags.total = restoredData.tags.length;
        
        // 创建ID映射和名称映射，避免ID冲突和名称重复
        const existingTagIds = new Set(tags.map(t => t.id));
        const existingTagNames = new Map(tags.map(t => [t.name.toLowerCase(), t]));
        
        const processedTags = restoredData.tags.filter(tag => {
          // 如果已经存在相同名称的标签，跳过
          const existingTag = existingTagNames.get(tag.name.toLowerCase());
          if (existingTag) {
            importStats.tags.skipped++;
            return false;
          }
          return true;
        }).map(tag => {
          // 如果ID已存在，生成新ID
          const newId = existingTagIds.has(tag.id) ? uuidv4() : tag.id;
          existingTagIds.add(newId);
          
          return {
            ...tag,
            id: newId
          };
        });
        
        importStats.tags.imported = processedTags.length;
        
        if (processedTags.length > 0) {
          setTags(prevTags => [...prevTags, ...processedTags]);
        }
      }
      
      // 生成导入统计信息
      const importSummary = `
        数据恢复成功！
        - 提示词: 导入 ${importStats.prompts.imported}/${importStats.prompts.total} (跳过 ${importStats.prompts.skipped} 个重复项)
        - 文件夹: 导入 ${importStats.folders.imported}/${importStats.folders.total} (跳过 ${importStats.folders.skipped} 个重复项)
        - 标签: 导入 ${importStats.tags.imported}/${importStats.tags.total} (跳过 ${importStats.tags.skipped} 个重复项)
      `;
      
      message.success(importSummary);
    } catch (error) {
      console.error('恢复数据失败:', error);
      message.error('恢复数据失败: ' + error.message);
    }
  };
  
  // 关闭云端备份弹窗
  const handleCloseCloudBackupModal = () => {
    setCloudBackupModal({
      visible: false
    });
  };

  // 侧边栏可见性状态
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // 切换侧边栏可见性
  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  // 初始化数据库并加载数据
  useEffect(() => {
    const initializeDB = async () => {
      try {
        setIsLoading(true);
        // 初始化数据库
        await DB.initDB();
        
        // 加载数据
        await loadData();
      } catch (error) {
        console.error('初始化数据库失败:', error);
        // 如果IndexedDB失败，尝试从localStorage加载
        loadFromLocalStorage();
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeDB();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 从IndexedDB加载所有数据
  const loadData = async () => {
    try {
      // 加载提示词
      const promptsData = await DB.getAllItems(DB.STORES.PROMPTS);
      setPrompts(promptsData || []);
      
      // 加载文件夹
      const foldersData = await DB.getAllItems(DB.STORES.FOLDERS);
      setFolders(foldersData || []);
      
      // 加载标签
      const tagsData = await DB.getAllItems(DB.STORES.TAGS);
      setTags(tagsData || []);
      
      // 加载收藏的提示词
      const favoritesData = await DB.getFavoritePrompts();
      setFavoritePrompts(favoritesData || []);
      
      console.log('从IndexedDB加载数据成功');
    } catch (error) {
      console.error('从IndexedDB加载数据失败:', {
        message: error.message || '未知错误',
        name: error.name,
        stack: error.stack,
        error: error
      });
      
      // 如果加载失败，尝试从localStorage加载
      loadFromLocalStorage();
    }
  };

  // 从localStorage加载数据（备用方案）
  const loadFromLocalStorage = () => {
    console.log('从localStorage加载数据...');
    
    // 加载提示词
    const savedPrompts = localStorage.getItem('prompts');
    setPrompts(savedPrompts ? JSON.parse(savedPrompts) : []);
    
    // 加载文件夹
    const savedFolders = localStorage.getItem('folders');
    setFolders(savedFolders ? JSON.parse(savedFolders) : []);
    
    // 加载标签
    const savedTags = localStorage.getItem('tags');
    setTags(savedTags ? JSON.parse(savedTags) : []);
    
    console.log('从localStorage加载数据成功');
  };

  // 将提示词数据保存到IndexedDB
  useEffect(() => {
    if (prompts.length > 0 && !isLoading) {
      const savePrompts = async () => {
        try {
          await DB.saveAllItems(DB.STORES.PROMPTS, prompts);
          console.log('提示词数据保存到IndexedDB成功');
        } catch (error) {
          console.error('保存提示词数据到IndexedDB失败:', error);
          // 备用：保存到localStorage
          localStorage.setItem('prompts', JSON.stringify(prompts));
        }
      };
      
      savePrompts();
    }
  }, [prompts, isLoading]);

  // 将文件夹数据保存到IndexedDB
  useEffect(() => {
    if (folders.length > 0 && !isLoading) {
      const saveFolders = async () => {
        try {
          await DB.saveAllItems(DB.STORES.FOLDERS, folders);
          console.log('文件夹数据保存到IndexedDB成功');
        } catch (error) {
          console.error('保存文件夹数据到IndexedDB失败:', error);
          // 备用：保存到localStorage
          localStorage.setItem('folders', JSON.stringify(folders));
        }
      };
      
      saveFolders();
    }
  }, [folders, isLoading]);

  // 将标签数据保存到IndexedDB
  useEffect(() => {
    if (tags.length > 0 && !isLoading) {
      const saveTags = async () => {
        try {
          await DB.saveAllItems(DB.STORES.TAGS, tags);
          console.log('标签数据保存到IndexedDB成功');
        } catch (error) {
          console.error('保存标签数据到IndexedDB失败:', error);
          // 备用：保存到localStorage
          localStorage.setItem('tags', JSON.stringify(tags));
        }
      };
      
      saveTags();
    }
  }, [tags, isLoading]);

  // 处理收藏状态变化
  const handleFavoriteChange = async (promptId, isFavorite) => {
    try {
      // 更新提示词的收藏状态
      const updatedPrompts = prompts.map(p => {
        if (p.id === promptId) {
          return { ...p, isFavorite };
        }
        return p;
      });
      
      setPrompts(updatedPrompts);
      
      // 更新数据库
      await DB.updateItem(DB.STORES.PROMPTS, { id: promptId, isFavorite });
      
      // 重新加载收藏提示词
      try {
        const favoritesData = await DB.getFavoritePrompts();
        setFavoritePrompts(favoritesData || []);
      } catch (error) {
        console.error('加载收藏提示词失败:', error);
      }
      
      message.success(isFavorite ? '已添加到收藏' : '已从收藏中移除');
    } catch (error) {
      console.error('更新收藏状态失败:', error);
      message.error('操作失败，请稍后重试');
    }
  };

  // 处理选择收藏的提示词
  const handleFavoriteSelect = (promptId) => {
    setSelectedPromptId(promptId);
    setSelectedFavorite(promptId);
    setSelectedFolderId(null);
    setSelectedTagIds([]);
    setEditMode(false);
    setCompareMode(false);
    setTimelineMode(false);
    setViewingFavorites(true);
  };

  // 处理清除收藏选择
  const clearFavoriteSelection = () => {
    setSelectedFavorite(null);
    setViewingFavorites(false);
  };

  // 添加新版本
  const addVersion = (versionData) => {
    if (!selectedPromptId) return;
    
    // 记录原始图片数据以便调试
    console.log('原始版本数据:', versionData);
    console.log('原始图片数据:', versionData.resultImages);
    
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === selectedPromptId) {
          // 定义当前版本号
          const currentVersionNumber = versionData.versionNumber || `${prompt.versions.length + 1}.0`;
          
          // 更可靠的图片处理逻辑
          const processedImages = [];
          
          // 确保结果图片存在并有效
          if (versionData.resultImages && Array.isArray(versionData.resultImages)) {
            versionData.resultImages.forEach(img => {
              // 确保从直接属性或响应中正确获取URL和key
              const url = img.url || (img.response && img.response.url);
              const key = img.key || (img.response && img.response.key);
              
              if (url && key) {
                processedImages.push({
                  url,
                  key,
                  uid: img.uid || `img-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`,
                  name: img.name || 'image.jpg',
                  versionNumber: currentVersionNumber // 记录图片归属的版本
                });
              } else {
                console.warn('跳过无效图片:', img);
              }
            });
          }
          
          const newVersion = {
            id: uuidv4(),
            content: versionData.content || prompt.versions[prompt.currentVersionIndex].content,
            description: versionData.description || `版本 ${prompt.versions.length + 1}`,
            createdAt: new Date().toISOString(),
            versionNumber: currentVersionNumber,
            tags: versionData.tags || [],
            // 每个版本保存独立的图片集合
            resultImages: processedImages
          };
          
          console.log(`添加新版本 ${newVersion.versionNumber} 的图片:`, processedImages);
          
          const newVersions = [...prompt.versions, newVersion];
          return {
            ...prompt,
            versions: newVersions,
            currentVersionIndex: newVersions.length - 1,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
    
    // 显示成功消息
    message.success('新版本添加成功');
    
    // 退出编辑模式
    setEditMode(false);
  };

  // 切换版本
  const switchVersion = (versionIndex) => {
    if (!selectedPromptId) return;
    
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === selectedPromptId) {
          return {
            ...prompt,
            currentVersionIndex: versionIndex,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
  };

  // 创建版本分支
  const createBranch = (promptId, versionIndex, branchData) => {
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === promptId) {
          const sourceVersion = prompt.versions[versionIndex];
          const newVersion = {
            id: uuidv4(),
            content: sourceVersion.content,
            description: branchData.description || `从版本 ${versionIndex + 1} 创建的分支`,
            createdAt: new Date().toISOString(),
            versionNumber: branchData.versionNumber || `${prompt.versions.length + 1}.0`,
            tags: branchData.tags || [],
            branchedFrom: versionIndex
          };
          const newVersions = [...prompt.versions, newVersion];
          return {
            ...prompt,
            versions: newVersions,
            currentVersionIndex: newVersions.length - 1,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
  };

  // 回滚到指定版本
  const rollbackVersion = (promptId, versionIndex) => {
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === promptId) {
          const rollbackVersion = prompt.versions[versionIndex];
          const newVersion = {
            id: uuidv4(),
            content: rollbackVersion.content,
            description: `回滚到版本 ${versionIndex + 1}`,
            createdAt: new Date().toISOString(),
            versionNumber: `${prompt.versions.length + 1}.0-rollback`,
            tags: ['回滚版本'],
            rollbackFrom: versionIndex
          };
          const newVersions = [...prompt.versions, newVersion];
          return {
            ...prompt,
            versions: newVersions,
            currentVersionIndex: newVersions.length - 1,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
  };

  // 添加版本标签
  const addVersionTag = (promptId, versionIndex, tag) => {
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === promptId) {
          const newVersions = [...prompt.versions];
          const version = newVersions[versionIndex];
          
          // 如果版本没有标签数组，创建一个
          if (!version.tags) {
            version.tags = [];
          }
          
          // 只有当标签不存在时才添加
          if (!version.tags.includes(tag)) {
            version.tags = [...version.tags, tag];
          }
          
          return {
            ...prompt,
            versions: newVersions,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
  };

  // 删除版本标签
  const removeVersionTag = (promptId, versionIndex, tag) => {
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === promptId) {
          const newVersions = [...prompt.versions];
          const version = newVersions[versionIndex];
          
          if (version.tags && version.tags.includes(tag)) {
            version.tags = version.tags.filter(t => t !== tag);
          }
          
          return {
            ...prompt,
            versions: newVersions,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
  };

  // 删除版本
  const deleteVersion = (promptId, versionIndex) => {
    setPrompts(prevPrompts => {
      return prevPrompts.map(prompt => {
        if (prompt.id === promptId) {
          // 如果只有一个版本，不允许删除
          if (prompt.versions.length <= 1) {
            return prompt;
          }
          
          // 创建新的版本数组，移除指定版本
          const newVersions = prompt.versions.filter((_, index) => index !== versionIndex);
          
          // 如果删除的是当前版本，则切换到最新版本
          let newCurrentVersionIndex = prompt.currentVersionIndex;
          if (versionIndex === prompt.currentVersionIndex) {
            newCurrentVersionIndex = newVersions.length - 1;
          } else if (versionIndex < prompt.currentVersionIndex) {
            // 如果删除的版本在当前版本之前，需要调整当前版本索引
            newCurrentVersionIndex = prompt.currentVersionIndex - 1;
          }
          
          return {
            ...prompt,
            versions: newVersions,
            currentVersionIndex: newCurrentVersionIndex,
            updatedAt: new Date().toISOString()
          };
        }
        return prompt;
      });
    });
  };

  // 合并版本
  const mergeVersions = (mergeData) => {
    if (!selectedPromptId) return;

    const prompt = prompts.find(p => p.id === selectedPromptId);
    if (!prompt) return;

    // 创建新的合并版本
    const newVersion = {
      id: uuidv4(),
      content: mergeData.content,
      description: mergeData.description || '合并版本',
      createdAt: new Date().toISOString(),
      versionNumber: mergeData.versionNumber || `${prompt.versions.length + 1}.0`,
      tags: [],
      mergedFrom: mergeData.mergedFrom || []
    };

    setPrompts(prevPrompts => {
      return prevPrompts.map(p => {
        if (p.id === selectedPromptId) {
          const updatedVersions = [...p.versions, newVersion];
          return {
            ...p,
            versions: updatedVersions,
            currentVersionIndex: updatedVersions.length - 1,
            updatedAt: new Date().toISOString()
          };
        }
        return p;
      });
    });

    setCompareMode(false);
  };

  // 进入版本比较模式
  const handleCompareVersions = () => {
    if (selectedPromptId) {
      const prompt = prompts.find(p => p.id === selectedPromptId);
      if (prompt && prompt.versions.length > 1) {
        setCompareVersions({
          leftVersionIndex: prompt.currentVersionIndex,
          rightVersionIndex: prompt.currentVersionIndex > 0 ? prompt.currentVersionIndex - 1 : 0
        });
        setCompareMode(true);
        setTimelineMode(false);
        setEditMode(false);
      }
    }
  };

  // 进入时间线模式
  const handleViewTimeline = () => {
    if (selectedPromptId) {
      setTimelineMode(true);
      setCompareMode(false);
      setEditMode(false);
    }
  };

  // 退出比较和时间线模式
  const handleExitSpecialMode = () => {
    setCompareMode(false);
    setTimelineMode(false);
  };

  // 更新比较的版本
  const updateCompareVersions = (leftIndex, rightIndex) => {
    setCompareVersions({
      leftVersionIndex: leftIndex,
      rightVersionIndex: rightIndex
    });
  };

  // 处理文件夹选择
  const handleFolderSelect = (folderId) => {
    setSelectedFolderId(folderId === selectedFolderId ? null : folderId);
    setSelectedTagIds([]);
  };

  // 处理标签选择
  const handleTagSelect = (tagId) => {
    setSelectedTagIds(prevSelectedTagIds => {
      if (prevSelectedTagIds.includes(tagId)) {
        return prevSelectedTagIds.filter(id => id !== tagId);
      } else {
        return [...prevSelectedTagIds, tagId];
      }
    });
    setSelectedFolderId(null);
  };

  // 添加文件夹
  const handleAddFolder = async () => {
    if (newFolderName.trim()) {
      try {
        // 检查是否已存在同名文件夹（不区分大小写）
        const folderExists = folders.some(folder => folder.name.toLowerCase() === newFolderName.trim().toLowerCase());
        
        if (folderExists) {
          // 如果文件夹已存在，显示提示信息
          message.warning(`文件夹"${newFolderName.trim()}"已存在，请使用其他名称`);
          return;
        }
        
        // 创建新文件夹对象
        const newFolder = { 
          id: uuidv4(), 
          name: newFolderName.trim(),
          createdAt: new Date().toISOString()
        };
        
        // 添加新文件夹到状态
        setFolders(prevFolders => [...prevFolders, newFolder]);
        
        // 保存新文件夹到数据库
        await DB.saveItem(DB.STORES.FOLDERS, newFolder);
        console.log(`新文件夹 ${newFolder.id} 已保存到数据库`);
        
        // 清空输入框并关闭添加文件夹表单
        setNewFolderName('');
        setShowAddFolder(false);
        
        // 显示成功消息
        message.success('文件夹添加成功');
      } catch (error) {
        console.error('添加文件夹失败:', error);
        message.error('文件夹添加失败，请重试');
      }
    }
  };

  // 添加标签
  const handleAddTag = async () => {
    if (newTagName.trim()) {
      try {
        // 检查是否已存在同名标签（不区分大小写）
        const tagExists = tags.some(tag => tag.name.toLowerCase() === newTagName.trim().toLowerCase());
        
        if (tagExists) {
          // 如果标签已存在，显示提示信息
          message.warning(`标签"${newTagName.trim()}"已存在，请使用其他名称`);
          return;
        }
        
        // 创建新标签对象
        const newTag = { 
          id: uuidv4(), 
          name: newTagName.trim(),
          createdAt: new Date().toISOString()
        };
        
        // 添加新标签到状态
        setTags(prevTags => [...prevTags, newTag]);
        
        // 保存新标签到数据库
        await DB.saveItem(DB.STORES.TAGS, newTag);
        console.log(`新标签 ${newTag.id} 已保存到数据库`);
        
        // 清空输入框并关闭添加标签表单
        setNewTagName('');
        setShowAddTag(false);
        
        // 显示成功消息
        message.success('标签添加成功');
      } catch (error) {
        console.error('添加标签失败:', error);
        message.error('标签添加失败，请重试');
      }
    }
  };

  // 批量删除提示词
  const batchDeletePrompts = async (promptIds) => {
    if (!promptIds || promptIds.length === 0) return;
    
    try {
      // 显示加载中提示
      const loadingMessage = message.loading(`正在删除 ${promptIds.length} 个提示词...`, 0);
      
      let successCount = 0;
      let failCount = 0;
      
      // 从数据库中批量删除提示词
      for (const promptId of promptIds) {
        try {
          await DB.deleteItem(DB.STORES.PROMPTS, promptId);
          
          // 同时从localStorage中删除
          try {
            const savedPrompts = localStorage.getItem('prompts');
            if (savedPrompts) {
              const promptsArray = JSON.parse(savedPrompts);
              const updatedPrompts = promptsArray.filter(prompt => prompt.id !== promptId);
              localStorage.setItem('prompts', JSON.stringify(updatedPrompts));
            }
          } catch (localStorageError) {
            console.error(`从localStorage删除提示词 ${promptId} 失败:`, localStorageError);
          }
          
          successCount++;
        } catch (error) {
          console.error(`删除提示词 ${promptId} 失败:`, error);
          failCount++;
        }
      }
      
      // 关闭加载提示
      loadingMessage();
      
      // 更新状态
      setPrompts(prevPrompts => prevPrompts.filter(prompt => !promptIds.includes(prompt.id)));
      
      // 如果当前选中的提示词被删除，清除选中状态
      if (selectedPromptId && promptIds.includes(selectedPromptId)) {
        setSelectedPromptId(null);
        setEditMode(false);
        setCompareMode(false);
        setTimelineMode(false);
      }
      
      // 显示结果消息
      if (failCount === 0) {
        message.success(`成功删除 ${successCount} 个提示词`);
      } else {
        message.warning(`成功删除 ${successCount} 个提示词，${failCount} 个删除失败`);
      }
    } catch (error) {
      console.error('批量删除提示词失败:', error);
      message.error('批量删除提示词失败，请重试');
    }
  };

  // 删除提示词
  const deletePrompt = async (promptId) => {
    try {
      // 显示加载中提示
      const loadingMessage = message.loading('正在删除提示词...', 0);
      
      // 从数据库中删除提示词
      const result = await DB.deleteItem(DB.STORES.PROMPTS, promptId);
      console.log(`提示词删除结果:`, result);
      
      // 同时从localStorage中删除（防止IndexedDB失败时从localStorage恢复）
      try {
        const savedPrompts = localStorage.getItem('prompts');
        if (savedPrompts) {
          const promptsArray = JSON.parse(savedPrompts);
          const updatedPrompts = promptsArray.filter(prompt => prompt.id !== promptId);
          localStorage.setItem('prompts', JSON.stringify(updatedPrompts));
          console.log(`提示词已从localStorage中删除`);
        }
      } catch (localStorageError) {
        console.error('从localStorage删除提示词失败:', localStorageError);
      }
      
      // 关闭加载提示
      loadingMessage();
      
      if (result.success) {
        // 删除成功后再更新状态
        setPrompts(prevPrompts => prevPrompts.filter(prompt => prompt.id !== promptId));
        
        // 如果当前选中的提示词被删除，清除选中状态
        if (selectedPromptId === promptId) {
          setSelectedPromptId(null);
          setEditMode(false);
          setCompareMode(false);
          setTimelineMode(false);
        }
        
        // 显示成功消息 - 注释掉这一行，避免显示两次成功消息
        // message.success('提示词删除成功');
        return true;
      } else {
        throw new Error(result.message || '删除失败');
      }
    } catch (error) {
      console.error(`从数据库中删除提示词 ${promptId} 失败:`, error);
      message.error('提示词删除失败，请重试');
      return false;
    }
  };

  // 删除文件夹
  const handleDeleteFolder = async (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    const folderName = folder ? folder.name : '此文件夹';
    
    showDeleteConfirm(
      `删除文件夹 "${folderName}"`,
      '确定要删除这个文件夹吗？文件夹中的提示词将被保留，但不再属于任何文件夹。',
      async () => {
        try {
          // 从数据库中删除文件夹
          await DB.deleteItem(DB.STORES.FOLDERS, folderId);
          console.log(`文件夹 ${folderId} 已从数据库中删除`);
          
          // 清除localStorage中的firstLoad标记，确保不会自动重新创建默认文件夹
          localStorage.removeItem('firstLoad');
          
          // 更新所有属于该文件夹的提示词
          const updatedPrompts = prompts.map(prompt => {
            // 检查提示词是否属于该文件夹
            if (prompt.folderId === folderId) {
              // 创建一个新的提示词对象，将folderId设为null
              return {
                ...prompt,
                folderId: null,
                updatedAt: new Date().toISOString()
              };
            }
            return prompt;
          });
          
          // 更新提示词数据
          setPrompts(updatedPrompts);
          
          // 将更新后的提示词保存到数据库
          try {
            await DB.saveAllItems(DB.STORES.PROMPTS, updatedPrompts);
            console.log('更新后的提示词数据保存到数据库成功');
          } catch (error) {
            console.error('保存更新后的提示词数据到数据库失败:', error);
          }
          
          // 删除成功后再更新文件夹状态
          setFolders(prevFolders => prevFolders.filter(folder => folder.id !== folderId));
          if (selectedFolderId === folderId) {
            setSelectedFolderId(null);
          }
          
          // 显示成功消息
          message.success('文件夹删除成功，相关提示词已更新');
        } catch (error) {
          console.error(`从数据库中删除文件夹 ${folderId} 失败:`, error);
          message.error('文件夹删除失败，请重试');
          throw error; // 抛出错误，让上层函数捕获
        }
      }
    );
  };

  // 删除标签
  const handleDeleteTag = async (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    const tagName = tag ? tag.name : '此标签';
    
    showDeleteConfirm(
      `删除标签 "${tagName}"`,
      '确定要删除这个标签吗？这将同时从所有提示词中移除该标签。',
      async () => {
        try {
          // 从数据库中删除标签
          await DB.deleteItem(DB.STORES.TAGS, tagId);
          console.log(`标签 ${tagId} 已从数据库中删除`);
          
          // 清除localStorage中的firstLoad标记，确保不会自动重新创建默认标签
          localStorage.removeItem('firstLoad');
          
          // 从所有提示词中移除该标签
          const updatedPrompts = prompts.map(prompt => {
            // 检查提示词是否使用了该标签
            if (prompt.tags && prompt.tags.includes(tagId)) {
              // 创建一个新的提示词对象，移除已删除的标签
              return {
                ...prompt,
                tags: prompt.tags.filter(id => id !== tagId),
                updatedAt: new Date().toISOString()
              };
            }
            return prompt;
          });
          
          // 更新提示词数据
          setPrompts(updatedPrompts);
          
          // 将更新后的提示词保存到数据库
          try {
            await DB.saveAllItems(DB.STORES.PROMPTS, updatedPrompts);
            console.log('更新后的提示词数据保存到数据库成功');
          } catch (error) {
            console.error('保存更新后的提示词数据到数据库失败:', error);
          }
          
          // 删除成功后再更新标签状态
          setTags(prevTags => prevTags.filter(tag => tag.id !== tagId));
          setSelectedTagIds(prevSelectedTagIds => prevSelectedTagIds.filter(id => id !== tagId));
          
          // 显示成功消息
          message.success('标签删除成功，并已从所有提示词中移除');
        } catch (error) {
          console.error(`从数据库中删除标签 ${tagId} 失败:`, error);
          message.error('标签删除失败，请重试');
          throw error; // 抛出错误，让上层函数捕获
        }
      }
    );
  };

  // 清除数据库中的所有标签和文件夹数据
  const clearAllTagsAndFolders = async () => {
    try {
      // 清除数据库中的标签数据
      await DB.clearStore(DB.STORES.TAGS);
      console.log('数据库中的标签数据已清除');
      
      // 清除数据库中的文件夹数据
      await DB.clearStore(DB.STORES.FOLDERS);
      console.log('数据库中的文件夹数据已清除');
      
      // 清除localStorage中的firstLoad标记，确保不会自动重新创建默认标签和文件夹
      localStorage.removeItem('firstLoad');
      
      // 更新状态
      setTags([]);
      setFolders([]);
      setSelectedTagIds([]);
      setSelectedFolderId(null);
      
      // 显示成功消息
      message.success('数据库中的标签和文件夹数据已清除');
    } catch (error) {
      console.error('清除数据库中的标签和文件夹数据失败:', error);
      message.error('清除数据库中的标签和文件夹数据失败，请重试');
    }
  };

  // 重置数据库（用于解决版本不匹配问题）
  const handleResetDatabase = async () => {
    try {
      // 显示确认对话框
      Modal.confirm({
        title: '确认重置数据库',
        content: '这将删除并重建数据库，可能导致数据丢失。建议先导出备份。确定要继续吗？',
        okText: '确认重置',
        cancelText: '取消',
        onOk: async () => {
          // 显示加载中提示
          const loadingMessage = message.loading('正在重置数据库...', 0);
          
          try {
            // 重置数据库
            await DB.resetDatabase();
            
            // 关闭加载提示
            loadingMessage();
            
            // 显示成功消息
            message.success('数据库重置成功，请刷新页面');
            
            // 清空本地状态
            setPrompts([]);
            setFolders([]);
            setTags([]);
            setSelectedPromptId(null);
            setEditMode(false);
            setCompareMode(false);
            setTimelineMode(false);
            
            // 3秒后刷新页面
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          } catch (error) {
            // 关闭加载提示
            loadingMessage();
            
            console.error('重置数据库失败:', error);
            message.error('重置数据库失败: ' + (error.message || '未知错误'));
          }
        }
      });
    } catch (error) {
      console.error('显示重置确认对话框失败:', error);
    }
  };

  // 编辑文件夹名称
  const handleEditFolder = async (folderId, newName) => {
    try {
      // 检查是否已存在同名文件夹（不区分大小写）
      const folderExists = folders.some(
        folder => folder.id !== folderId && folder.name.toLowerCase() === newName.toLowerCase()
      );
      
      if (folderExists) {
        // 如果文件夹已存在，显示提示信息
        message.warning(`文件夹"${newName}"已存在，请使用其他名称`);
        return;
      }
      
      // 更新文件夹状态
      setFolders(prevFolders => {
        return prevFolders.map(folder => {
          if (folder.id === folderId) {
            return {
              ...folder,
              name: newName,
              updatedAt: new Date().toISOString()
            };
          }
          return folder;
        });
      });
      
      // 从数据库中获取文件夹
      const folder = await DB.getItem(DB.STORES.FOLDERS, folderId);
      
      if (folder) {
        // 更新文件夹名称
        const updatedFolder = {
          ...folder,
          name: newName,
          updatedAt: new Date().toISOString()
        };
        
        // 保存更新后的文件夹到数据库
        await DB.updateItem(DB.STORES.FOLDERS, updatedFolder);
        console.log(`文件夹 ${folderId} 已重命名为 "${newName}"`);
        
        // 显示成功消息
        message.success('文件夹重命名成功');
      } else {
        throw new Error(`找不到ID为 ${folderId} 的文件夹`);
      }
    } catch (error) {
      console.error(`重命名文件夹 ${folderId} 失败:`, error);
      message.error('文件夹重命名失败，请重试');
    }
  };

  // 添加新提示词
  const handleAddPrompt = async () => {
    // 检查是否正在创建中
    if (editMode && selectedPromptId) {
      const currentPrompt = prompts.find(p => p.id === selectedPromptId);
      if (currentPrompt && currentPrompt.title === '新提示词' && !currentPrompt.versions[0].content) {
        // 如果已经在编辑一个空白提示词，直接返回，不再创建新的
        return;
      }
    }
    
    // 检查是否已有一个空白的新提示词
    const emptyPrompt = prompts.find(p => 
      (p.title === '新提示词' && !p.versions[0].content) || 
      (!p.title.trim() && !p.versions[0].content)
    );
    
    if (emptyPrompt) {
      console.log('已存在空白提示词，将其选中:', emptyPrompt.id);
      setSelectedPromptId(emptyPrompt.id);
      setEditMode(true);
      return;
    }
    
    try {
      // 创建新提示词对象
      const newPrompt = {
        id: uuidv4(),
        title: '新提示词',
        introduction: '', 
        folderId: selectedFolderId || null,
        tags: selectedTagIds.length > 0 ? [...selectedTagIds] : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentVersionIndex: 0,
        versions: [
          {
            id: uuidv4(),
            content: '',
            description: '初始版本',
            createdAt: new Date().toISOString(),
            versionNumber: '1.0',
            tags: [],
            resultImages: []
          }
        ]
      };
      
      console.log('创建新提示词:', newPrompt);
      
      // 检查是否有无效的空白提示词，如果有则删除
      const invalidPrompts = prompts.filter(p => 
        (!p.title.trim() && !p.versions[0].content) || 
        (p.title === '新提示词' && !p.versions[0].content)
      );
      
      if (invalidPrompts.length > 0) {
        console.log(`发现 ${invalidPrompts.length} 个无效的空白提示词，准备删除`);
        
        // 从数据库中删除无效提示词
        for (const prompt of invalidPrompts) {
          try {
            await DB.deleteItem(DB.STORES.PROMPTS, prompt.id);
            console.log(`已从数据库中删除无效提示词: ${prompt.id}`);
          } catch (err) {
            console.error(`删除无效提示词失败: ${prompt.id}`, err);
          }
        }
        
        // 从状态中移除无效提示词
        setPrompts(prevPrompts => prevPrompts.filter(p => 
          !invalidPrompts.some(invalid => invalid.id === p.id)
        ));
      }
      
      // 先保存到数据库，确保数据持久化
      await DB.saveItem(DB.STORES.PROMPTS, newPrompt);
      
      // 同时保存到localStorage（防止IndexedDB失败时数据丢失）
      try {
        const savedPrompts = localStorage.getItem('prompts');
        const promptsArray = savedPrompts ? JSON.parse(savedPrompts) : [];
        promptsArray.push(newPrompt);
        localStorage.setItem('prompts', JSON.stringify(promptsArray));
        console.log('新提示词已同步保存到localStorage');
      } catch (localStorageError) {
        console.error('保存提示词到localStorage失败:', localStorageError);
      }
      
      // 更新状态，添加新提示词
      setPrompts(prevPrompts => [...prevPrompts, newPrompt]);
      
      // 选中新创建的提示词并进入编辑模式
      setSelectedPromptId(newPrompt.id);
      setEditMode(true);
    } catch (error) {
      console.error('创建新提示词失败:', error);
      message.error('创建新提示词失败，请重试');
    }
  };

  // 处理取消编辑提示词
  const handleCancelEdit = async (prompt) => {
    console.log("取消编辑提示词", prompt);
    // 检查是否是新创建的提示词（内容为空）
    if (prompt && prompt.versions[0].content === '') {
      try {
        // 从数据库中删除空白提示词
        await DB.deleteItem(DB.STORES.PROMPTS, prompt.id);
        console.log(`空白提示词 ${prompt.id} 已从数据库中删除`);
        
        // 从状态中删除空白提示词
        setPrompts(prevPrompts => prevPrompts.filter(p => p.id !== prompt.id));
        setSelectedPromptId(null);
        
        // 显示成功消息
        message.success('已取消创建提示词');
      } catch (error) {
        console.error(`删除空白提示词 ${prompt.id} 失败:`, error);
        // 即使删除失败，也从状态中移除
        setPrompts(prevPrompts => prevPrompts.filter(p => p.id !== prompt.id));
        setSelectedPromptId(null);
      }
    }
    // 关闭编辑模式
    setEditMode(false);
  };

  // 保存编辑后的提示词
  const handleSavePrompt = async (editedPrompt) => {
    console.log('保存提示词数据:', editedPrompt); 

    try {
      // 显示加载中提示
      const loadingMessage = message.loading('正在保存提示词...', 0);

      // 如果是编辑现有提示词
      if (selectedPromptId) {
        console.log('更新已有提示词:', selectedPromptId);
        
        // 先获取当前提示词
        const currentPrompt = prompts.find(p => p.id === selectedPromptId);
        if (!currentPrompt) {
          throw new Error('找不到要编辑的提示词');
        }
        
        // 创建更新后的提示词对象
        const updatedPrompt = {
          ...currentPrompt,
          title: editedPrompt.title || currentPrompt.title,
          introduction: editedPrompt.introduction || currentPrompt.introduction || '',
          folderId: editedPrompt.folderId,
          tags: editedPrompt.tags || currentPrompt.tags || [],
          updatedAt: new Date().toISOString()
        };
        
        // 更新当前版本的内容和版本号
        if (editedPrompt.content !== undefined) {
          // 不再使用现有图片，完全根据编辑器中的图片列表决定
          
          // 处理新上传的图片
          const newUploadedImages = editedPrompt.resultImages ? editedPrompt.resultImages.map(img => ({
            url: img.url || img.response?.url,
            key: img.key || img.response?.key,
            uid: img.uid,
            name: img.name,
            versionNumber: editedPrompt.versionNumber || updatedPrompt.versions[updatedPrompt.currentVersionIndex].versionNumber
          })) : [];
          
          // 完全使用编辑界面传入的图片列表，而不是合并
          // 这样删除的图片就不会再出现
          const mergedImages = [...newUploadedImages];
          
          console.log('编辑后的图片列表:', mergedImages);
          
          // 更新当前版本
          updatedPrompt.versions[updatedPrompt.currentVersionIndex] = {
            ...updatedPrompt.versions[updatedPrompt.currentVersionIndex],
            content: editedPrompt.content,
            versionNumber: editedPrompt.versionNumber || updatedPrompt.versions[updatedPrompt.currentVersionIndex].versionNumber,
            resultImages: mergedImages
          };
          
          console.log('更新版本的图片列表:', mergedImages);
        }
        
        // 保存到IndexedDB
        await DB.updateItem(DB.STORES.PROMPTS, updatedPrompt);
        console.log('提示词已保存到IndexedDB');
        
        // 同时更新localStorage
        try {
          const savedPrompts = localStorage.getItem('prompts');
          if (savedPrompts) {
            const promptsArray = JSON.parse(savedPrompts);
            const updatedPromptsArray = promptsArray.map(p => 
              p.id === updatedPrompt.id ? updatedPrompt : p
            );
            localStorage.setItem('prompts', JSON.stringify(updatedPromptsArray));
            console.log('提示词已同步更新到localStorage');
          }
        } catch (localStorageError) {
          console.error('更新提示词到localStorage失败:', localStorageError);
        }
        
        // 更新React状态
        setPrompts(prevPrompts => prevPrompts.map(prompt => 
          prompt.id === selectedPromptId ? updatedPrompt : prompt
        ));
        
        // 关闭加载提示
        loadingMessage();
        
        // 显示成功消息
        message.success('提示词保存成功');
      } 
      // 这个分支应该不会再被执行，因为我们总是先创建提示词，再编辑
      else {
        console.error('错误：尝试保存没有ID的提示词。这种情况不应发生。', editedPrompt);
        loadingMessage();
        message.error('保存提示词时发生错误，请刷新页面后重试');
      }
      
      setEditMode(false);
    } catch (error) {
      console.error('保存提示词失败:', error);
      message.error('保存提示词失败: ' + (error.message || '未知错误'));
    }
  };

  // 将提示词移动到文件夹
  const handleMoveToFolder = async (promptId, folderId) => {
    try {
      // 显示加载中提示
      const loadingMessage = message.loading('正在移动提示词...', 0);
      
      // 找到要移动的提示词
      const promptToMove = prompts.find(p => p.id === promptId);
      if (!promptToMove) {
        throw new Error('找不到要移动的提示词');
      }
      
      // 创建更新后的提示词对象
      const updatedPrompt = {
        ...promptToMove,
        folderId: folderId,
        updatedAt: new Date().toISOString()
      };
      
      // 保存到IndexedDB
      await DB.updateItem(DB.STORES.PROMPTS, updatedPrompt);
      console.log('提示词已移动到文件夹，已保存到IndexedDB');
      
      // 同时更新localStorage
      try {
        const savedPrompts = localStorage.getItem('prompts');
        if (savedPrompts) {
          const promptsArray = JSON.parse(savedPrompts);
          const updatedPromptsArray = promptsArray.map(p => 
            p.id === promptId ? updatedPrompt : p
          );
          localStorage.setItem('prompts', JSON.stringify(updatedPromptsArray));
          console.log('提示词移动已同步到localStorage');
        }
      } catch (localStorageError) {
        console.error('更新提示词到localStorage失败:', localStorageError);
      }
      
      // 更新React状态
      setPrompts(prevPrompts => {
        return prevPrompts.map(prompt => {
          if (prompt.id === promptId) {
            return updatedPrompt;
          }
          return prompt;
        });
      });
      
      // 关闭加载提示
      loadingMessage();
      
      // 显示成功消息
      message.success('提示词已移动到文件夹');
    } catch (error) {
      console.error('移动提示词失败:', error);
      message.error('移动提示词失败: ' + (error.message || '未知错误'));
    }
  };

  // 返回提示词列表
  const handleBackToList = () => {
    setSelectedPromptId(null);
    setEditMode(false);
    setCompareMode(false);
    setTimelineMode(false);
  };

  // 获取当前选中的提示词
  const selectedPrompt = prompts.find(prompt => prompt.id === selectedPromptId);

  // 添加新标签（从提示词编辑器）
  const handleAddNewTag = (newTag) => {
    // 检查是否已存在同名标签（不区分大小写）
    const tagExists = tags.some(tag => tag.name.toLowerCase() === newTag.name.toLowerCase());
    
    if (tagExists) {
      // 如果标签已存在，显示提示信息
      message.warning(`标签"${newTag.name}"已存在，请使用其他名称`);
      return;
    }
    
    setTags(prevTags => [...prevTags, newTag]);
  };

  // 导入备份
  const handleImportPrompts = useCallback((importedPrompts) => {
    if (!importedPrompts || importedPrompts.length === 0) return;
    
    console.log(`恢复 ${importedPrompts.length} 个提示词:`, importedPrompts);
    
    // 添加导入的提示词到现有提示词列表
    setPrompts(prevPrompts => {
      // 创建ID映射，避免ID冲突
      const existingIds = new Set(prevPrompts.map(p => p.id));
      // 记录新创建的标签，避免在同一次导入中创建重复标签
      const newTagsMap = new Map();
      
      // 处理每个导入的提示词
      const processedPrompts = importedPrompts.map(prompt => {
        // 如果ID已存在，生成新ID
        const newId = existingIds.has(prompt.id) ? uuidv4() : prompt.id;
        existingIds.add(newId);
        
        // 确保版本ID不冲突并处理标签
        const versions = prompt.versions.map(version => {
          // 处理版本中的标签
          const processedTags = Array.isArray(version.tags) 
            ? version.tags.map(tag => {
                // 如果标签是字符串而不是ID，尝试查找或创建对应的标签
                if (typeof tag === 'string' && !tag.includes('-') && tag.length < 30) {
                  // 查找是否有同名标签（不区分大小写）
                  const existingTag = tags.find(t => t.name.toLowerCase() === tag.toLowerCase());
                  if (existingTag) {
                    return existingTag.id;
                  }
                  
                  // 查找是否在本次导入中已创建同名标签
                  const lowercaseTag = tag.toLowerCase();
                  if (newTagsMap.has(lowercaseTag)) {
                    return newTagsMap.get(lowercaseTag);
                  }
                  
                  // 如果没有，将在下面的代码中创建新标签
                }
                return tag;
              })
            : [];
            
          return {
            ...version,
            id: uuidv4(), // 始终为版本生成新ID
            tags: processedTags
          };
        });
        
        // 处理提示词级别的标签
        const processedPromptTags = Array.isArray(prompt.tags) 
          ? prompt.tags.map(tag => {
              // 如果标签是字符串而不是ID，尝试查找或创建对应的标签
              if (typeof tag === 'string' && !tag.includes('-') && tag.length < 30) {
                // 查找是否有同名标签（不区分大小写）
                const existingTag = tags.find(t => t.name.toLowerCase() === tag.toLowerCase());
                if (existingTag) {
                  return existingTag.id;
                }
                
                // 查找是否在本次导入中已创建同名标签
                const lowercaseTag = tag.toLowerCase();
                if (newTagsMap.has(lowercaseTag)) {
                  return newTagsMap.get(lowercaseTag);
                }
                
                // 如果没有，创建新标签
                const newTagId = uuidv4();
                // 记录新创建的标签
                newTagsMap.set(lowercaseTag, newTagId);
                
                // 使用setTimeout确保在当前渲染循环后执行，避免状态更新冲突
                setTimeout(() => {
                  setTags(prevTags => {
                    // 再次检查是否已存在同名标签（可能在setTimeout期间被其他操作创建）
                    if (prevTags.some(t => t.name.toLowerCase() === tag.toLowerCase())) {
                      return prevTags;
                    }
                    return [...prevTags, { id: newTagId, name: tag }];
                  });
                }, 0);
                return newTagId;
              }
              return tag;
            })
          : [];
        
        return {
          ...prompt,
          id: newId,
          versions,
          tags: processedPromptTags,
          createdAt: prompt.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      });
      
      return [...prevPrompts, ...processedPrompts];
    });
  }, [tags]);

  // 将导入方法暴露给全局，以便ImportPrompts组件可以调用
  useEffect(() => {
    window.addImportedPrompts = handleImportPrompts;
    
    // 清理函数
    return () => {
      window.addImportedPrompts = undefined;
    };
  }, [handleImportPrompts]);

  // 获取每个文件夹中的提示词数量
  const getFolderCounts = () => {
    const counts = {};
    // 添加所有提示词的总数
    counts['all'] = prompts.length;
    
    // 计算每个文件夹中的提示词数量
    folders.forEach(folder => {
      counts[folder.id] = prompts.filter(prompt => prompt.folderId === folder.id).length;
    });
    
    // 计算未分类的提示词数量
    counts['uncategorized'] = prompts.filter(prompt => !prompt.folderId).length;
    
    return counts;
  };

  // 获取每个标签的使用数量
  const getTagCounts = () => {
    const counts = {};
    tags.forEach(tag => {
      counts[tag.id] = prompts.filter(prompt => prompt.tags && prompt.tags.includes(tag.id)).length;
    });
    return counts;
  };

  // 根据搜索、文件夹和标签过滤提示词
  const filteredPrompts = prompts.filter(prompt => {
    // 如果正在查看收藏，则只显示收藏的提示词
    if (viewingFavorites) {
      return favoritePrompts.some(fav => fav.id === prompt.id);
    }
    
    const matchesSearch = searchTerm === '' || 
      prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.versions[prompt.currentVersionIndex].content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFolder = selectedFolderId === null || prompt.folderId === selectedFolderId;
    
    const matchesTags = selectedTagIds.length === 0 || 
      (prompt.tags && selectedTagIds.every(tagId => prompt.tags.includes(tagId)));
    
    return matchesSearch && matchesFolder && matchesTags;
  });

  // 选择提示词
  const handlePromptSelect = (promptId) => {
    setSelectedPromptId(promptId);
    setEditMode(false);
    setCompareMode(false);
    setTimelineMode(false);
  };

  // 如果数据正在加载，显示加载状态
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>正在加载数据...</p>
      </div>
    );
  }

  // 渲染主应用界面
  return (
    <div className="app-container h-screen flex flex-col">
      <Header 
        searchTerm={searchTerm} 
        onSearch={setSearchTerm} 
        onCloudBackup={handleCloudBackup}
      />
      
      <div className="flex flex-1 overflow-hidden">
        {sidebarVisible && (
          <Sidebar 
            folders={folders}
            tags={tags}
            selectedFolderId={selectedFolderId}
            selectedTagIds={selectedTagIds}
            onFolderSelect={handleFolderSelect}
            onTagSelect={handleTagSelect}
            onClearSelection={() => {
              setSelectedFolderId(null);
              setSelectedTagIds([]);
              setSearchTerm('');
              setViewingFavorites(false);
              setSelectedFavorite(null);
            }}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            onSaveFolder={handleAddFolder}
            showAddFolder={showAddFolder}
            setShowAddFolder={setShowAddFolder}
            newTagName={newTagName}
            setNewTagName={setNewTagName}
            onSaveTag={handleAddTag}
            showAddTag={showAddTag}
            setShowAddTag={setShowAddTag}
            onDeleteFolder={handleDeleteFolder}
            onDeleteTag={handleDeleteTag}
            onAddFolder={() => setShowAddFolder(true)}
            onAddTag={() => setShowAddTag(true)}
            onEditFolder={handleEditFolder}
            folderCounts={getFolderCounts()}
            tagCounts={getTagCounts()}
            onClearAllTagsAndFolders={clearAllTagsAndFolders}
            favoritePrompts={favoritePrompts}
            onFavoriteSelect={handleFavoriteSelect}
            viewingFavorites={viewingFavorites}
            onClearFavoriteSelection={clearFavoriteSelection}
            selectedFavorite={selectedFavorite}
            onResetDatabase={handleResetDatabase}
          />
        )}
        
        <div className="flex-1 overflow-auto">
          <div className="p-6 h-full">
            <div className="flex items-center mb-4">
              <button 
                onClick={toggleSidebar}
                className="mr-4 text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)] transition-colors"
              >
                {sidebarVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              </button>
              {!sidebarVisible && <h2 className="text-lg font-medium text-[var(--apple-text-primary)]">我的提示词</h2>}
            </div>
            
            {/* 备份提醒组件 */}
            <BackupReminder onExport={() => handleExportAll('zip')} />
            
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">加载中...</div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                {!selectedPromptId ? (
                  <PromptList 
                    prompts={filteredPrompts}
                    onSelectPrompt={handlePromptSelect}
                    onCreatePrompt={handleAddPrompt}
                    onDeletePrompt={deletePrompt}
                    onBatchDeletePrompts={batchDeletePrompts}
                    folders={folders}
                    onMoveToFolder={handleMoveToFolder}
                    tags={tags}
                    onAddFolder={handleAddFolder}
                    onAddTag={handleAddTag}
                    onFavoriteChange={handleFavoriteChange}
                  />
                ) : (
                  <>
                    {editMode ? (
                      <PromptEditor 
                        mode={selectedPrompt.versions[0].content ? 'edit' : 'create'}
                        prompt={selectedPrompt}
                        onSave={handleSavePrompt}
                        onCancel={() => handleCancelEdit(selectedPrompt)}
                        folders={folders}
                        selectedFolder={selectedPrompt.folderId}
                        tags={tags}
                        onAddNewTag={handleAddNewTag}
                      />
                    ) : compareMode ? (
                      <VersionCompare
                        prompt={selectedPrompt}
                        leftVersionIndex={compareVersions.leftVersionIndex}
                        rightVersionIndex={compareVersions.rightVersionIndex}
                        onUpdateCompareVersions={updateCompareVersions}
                        onBack={handleExitSpecialMode}
                        onMergeVersions={mergeVersions}
                      />
                    ) : timelineMode ? (
                      <VersionTimeline
                        prompt={selectedPrompt}
                        onBack={handleExitSpecialMode}
                        onSwitchVersion={switchVersion}
                        onAddVersionTag={addVersionTag}
                        onRemoveVersionTag={removeVersionTag}
                        onCreateBranch={createBranch}
                        onRollbackVersion={rollbackVersion}
                        onDeleteVersion={deleteVersion}
                      />
                    ) : (
                      <PromptDetail 
                        prompt={selectedPrompt}
                        onBack={handleBackToList}
                        onEdit={() => setEditMode(true)}
                        onDelete={() => deletePrompt(selectedPrompt.id)}
                        onAddVersion={addVersion}
                        onSwitchVersion={switchVersion}
                        folders={folders}
                        onMoveToFolder={(folderId) => handleMoveToFolder(selectedPrompt.id, folderId)}
                        onCompareVersions={handleCompareVersions}
                        onViewTimeline={handleViewTimeline}
                        onCreateBranch={(versionIndex, branchData) => createBranch(selectedPrompt.id, versionIndex, branchData)}
                        onRollbackVersion={(versionIndex) => rollbackVersion(selectedPrompt.id, versionIndex)}
                        onDeleteVersion={(versionIndex) => deleteVersion(selectedPrompt.id, versionIndex)}
                        tags={tags}
                        onUpdatePrompt={handleUpdatePrompt}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* 删除确认弹窗 */}
      <Modal
        title={deleteModal.title}
        open={deleteModal.visible}
        onOk={handleDeleteConfirm}
        confirmLoading={deleteModal.loading}
        onCancel={handleDeleteCancel}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>{deleteModal.content}</p>
      </Modal>
      
      {/* 关闭标签页提示弹窗 */}
      <Modal
        title="关闭标签页提示"
        open={showCloseConfirm}
        onOk={() => setShowCloseConfirm(false)}
        onCancel={() => setShowCloseConfirm(false)}
        okText="确认关闭"
        cancelText="取消"
      >
        <p>是否需要备份当前数据？</p>
      </Modal>
      
      {/* 云端备份弹窗 */}
      <CloudBackup
        visible={cloudBackupModal.visible}
        onClose={handleCloseCloudBackupModal}
        onBackup={CloudBackupService.backup}
        onRestore={handleCloudRestore}
        data={{
          prompts: prompts,
          folders: folders,
          tags: tags
        }}
      />
    </div>
  );
}

// 路由组件
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/app" element={<MainApp />} />
        <Route path="/share/:shareId" element={<SharedPromptView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
